import React, { useCallback, useState } from 'react'
import { Stack, Box, Button, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { UserVehicle } from '../models'
import { useRouting, useVehiclesConnectingToWallboxSocket, useVehiclesInformationSocket } from '../hooks'
import VehicleTileContainer from './vehicle-tile/VehicleTileContainer'
import { useTypedSelector } from 'src/State/RootReducer'
import useFetchDataForWidgets from 'src/Libs/dashboard-widgets/hooks/use-fetch-data-for-widgets'
import { VehicleConnectingToWallboxIndicator } from './skeleton'
import { ChargingLocationMapModal } from './charging-location-map-modal'
import { useUserRoles } from 'src/Hooks/user'

const useStyles = makeStyles()(theme => ({
  generalLayout: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
  },
}))

interface Props {
  vehicles: UserVehicle[]
  installationId?: string
}

/**
 * A list component with connected vehicles
 */
const Vehicles: React.FC<Props> = ({ vehicles, installationId }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()

  const vehiclesIdentificationSocket = useVehiclesConnectingToWallboxSocket(installationId ? Number(installationId) : undefined)
  /* This websocket returns values only if the vehicle is connected to the wallbox */
  const vehiclesInformationSocket = useVehiclesInformationSocket(installationId ? Number(installationId) : undefined)
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const { wallboxWidgetsData } = useFetchDataForWidgets({ installationId, storageId })
  const { mappedWallboxesForWidget } = wallboxWidgetsData
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { navigateToAddVehiclePage } = useRouting()
  const [isChargingLocationMapModalOpen, setIsChargingLocationMapModalOpen] = useState<boolean>(false)
  const { isServiceUser } = useUserRoles()

  /* check if a vehicle is in the process of connecting to a wallbox */
  const isVehicleConnectingToWallbox = vehiclesIdentificationSocket.data?.inProgress && vehiclesIdentificationSocket.data?.wallboxIds.length > 0

  const handleAddVehicle = (): void => {
    navigateToAddVehiclePage()
  }

  /* use callback to minimize re-renders of map in the location modal. otherwise cause flickering */
  const handleCloseChargingLocationMapModal = useCallback((): void => {
    setIsChargingLocationMapModalOpen(false)
  }, [])

  const handleOpenChargingLocationMapModal = (): void => {
    setIsChargingLocationMapModalOpen(true)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isVehicleConnectingToWallbox && <VehicleConnectingToWallboxIndicator />}
      {isChargingLocationMapModalOpen && installationId && (
        <ChargingLocationMapModal isModalOpen={isChargingLocationMapModalOpen} onClose={handleCloseChargingLocationMapModal} />
      )}

      <Box className={classes.generalLayout}>
        {vehicles.map(vehicle => {
          const connectedVehicle = vehiclesInformationSocket.data?.find(
            vehicleToFind => vehicleToFind.chargingState !== 'UNPLUGGED' && vehicleToFind.vehicleId === vehicle.id
          )
          const connectedWallbox = mappedWallboxesForWidget.find(wallbox => wallbox.wallboxId === connectedVehicle?.wallboxId)

          return (
            <VehicleTileContainer
              installationId={installationId}
              key={vehicle.id}
              vehicle={vehicle}
              connectedVehicle={connectedVehicle}
              connectedWallbox={connectedWallbox}
            />
          )
        })}
      </Box>

      <Stack direction="row" justifyContent={upMd ? 'flex-start' : 'center'} gap={2}>
        <Button disabled={isServiceUser} variant="contained" onClick={handleAddVehicle}>
          {t('emobility.vehicle-integration.add-vehicle')}
        </Button>
        <Button onClick={handleOpenChargingLocationMapModal}>{t('vehicle-integration.charging-location-map-modal.open-modal-button')}</Button>
      </Stack>
    </Box>
  )
}

export default Vehicles
